import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import Select from 'react-select';
import Creatable from "react-select/creatable";
import CheckIcon from '@mui/icons-material/Check';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DoneAllOutlinedIcon from '@mui/icons-material/DoneAllOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import LoopIcon from '@mui/icons-material/Loop';
import "./edit.scss";
import axiosInstance from "../../api/axiosInstance";

const Edit = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const wineSlug = location.pathname.split("/")[2];
    const [wine, setWine] = useState(null);
    const [isLoading, setLoading] = useState(true);
    const [selectedFile, setSelectedFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [alert, setAlert] = useState(null);
    const [countryOptions, setCountryOptions] = useState([]);
    const [regionOptions, setRegionOptions] = useState([]);
    const [wineryOptions, setWineryOptions] = useState([]);
    const [isUploading, setIsUploading] = useState(false);

    const [formData, setFormData] = useState({
        name: "",
        sorte: [],
        weinsorte: "",
        land: "",
        region: "",
        weingut: "",
        preis: "",
        info: "",
        img: "",
        img_back: "",
        status: "Lagernd",
    });

    const fetchData = useCallback(async () => {
        if (!wineSlug) return;

        try {
            const [wineRes, countriesRes, regionsRes, wineriesRes] = await Promise.all([
                axiosInstance.get(`wines/${wineSlug}`),
                axiosInstance.get('forms/countries'),
                axiosInstance.get('forms/regions'),
                axiosInstance.get('forms/wineries')
            ]);
            setWine(wineRes.data);
            setCountryOptions(countriesRes.data);
            setRegionOptions(regionsRes.data);
            setWineryOptions(wineriesRes.data);
            setFormData({
                name: wineRes.data.name || "",
                sorte: wineRes.data.sorte ? wineRes.data.sorte.split(",") : [],
                weinsorte: wineRes.data.weinsorte || "",
                land: wineRes.data.land || "",
                region: wineRes.data.region || "",
                weingut: wineRes.data.weingut || "",
                preis: wineRes.data.preis || "",
                info: wineRes.data.info || "",
                img: wineRes.data.img || "",
                img_back: wineRes.data.img_back || "",
                status: wineRes.data.status || "Lagernd",
            });
        } catch (err) {
            console.log("Fehler beim Laden der Daten:", err);
        } finally {
            setLoading(false);
        }
    }, [wineSlug]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        setSelectedFile(file);
        setPreview(URL.createObjectURL(file));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        let uploadedImageFilename = formData.img;

        setIsUploading(true);

        // 1. Bild hochladen (falls vorhanden)
        if (selectedFile) {
            const imageData = new FormData();
            imageData.append("image", selectedFile);

            try {
                const uploadRes = await axiosInstance.post('uploads', imageData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                uploadedImageFilename = uploadRes.data.filename;
            } catch (uploadErr) {
                console.error("Bild-Upload fehlgeschlagen:", uploadErr);
                return alert("Fehler beim Hochladen des Bildes");
            }
        }

        // 2. Weindaten speichern
        const payload = {
            ...formData,
            sorte: formData.sorte.join(","),
            img: uploadedImageFilename,
            img_back: formData.img_back || "",
        };

        try {
            let response;
            if (wine?.id) {
                response = await axiosInstance.put(`wines/${wine.id}`, payload);
            } else {
                response = await axiosInstance.post('wines', payload);
            }

            setAlert(response.data.message);
            setIsUploading(false);
            setTimeout(() => setAlert(null), 5000);
        } catch (err) {
            setAlert(err);
            setIsUploading(false);
            setTimeout(() => setAlert(null), 5000);
        }
    };

    const NoWine = () => (
        <div className="view">
            <div className="container">
                <div className="headingNotFound">
                    <img src="/assets/logo.png" alt="Logo" />
                    <h2>Wein nicht gefunden</h2>
                    <p>Es scheint, dass der Wein nicht mehr verfügbar ist oder nicht mehr geteilt wird.</p>
                </div>
            </div>
        </div>
    );

    return isLoading ? (<div className="loading" />) : (!wine && wineSlug) ? (<NoWine />) : (
        <motion.div
            className="edit"
            initial={{ scale: 0.9 }}
            whileInView={{ scale: 1 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
        >
            <Helmet>
                <title>{wine.name || "Neu"} | Weinkeller</title>
            </Helmet>
            {alert &&
                <div className="alert">
                    <DoneAllOutlinedIcon />
                    <span>{alert}</span>
                    <div className="line" />
                </div>
            }
            <div className="container">
                <div className="top">
                    <div className="left">
                        <input
                            type="file"
                            id="image-upload"
                            onChange={handleFileChange}
                            style={{ display: "none" }}
                        />
                        <label htmlFor="image-upload" className="custom-file-upload">
                            <AddCircleOutlineIcon />
                            <span>Bild hinzufügen</span>
                        </label>
                        <img src={preview || `https://api.weinkeller.site/uploads/${formData.img}`} alt="Weinfoto" />
                    </div>
                    <div className="right">
                        <div className="options">
                            {isUploading
                                ? <button disabled><LoopIcon className="rotate" /></button>
                                : <button type="submit" onClick={handleSubmit}><CheckIcon /></button>
                            }
                            <button onClick={() => navigate(-1)}><ClearIcon /></button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <input
                                type="text"
                                placeholder="Weinname..."
                                className="title"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                            />
                            <Creatable
                                className="select"
                                classNamePrefix="select"
                                isClearable
                                isMulti
                                placeholder="Rebsorten"
                                name="sorte"
                                value={formData.sorte.map(s => ({ value: s, label: s }))}
                                onChange={(selected) => handleSelectChange("sorte", selected ? selected.map(s => s.value) : [])}
                            />
                            <Select
                                className="select"
                                classNamePrefix="select"
                                isClearable
                                placeholder="Weinsorte"
                                name="weinsorte"
                                value={formData.weinsorte ? { value: formData.weinsorte, label: formData.weinsorte } : null}
                                onChange={(selected) => handleSelectChange("weinsorte", selected ? selected.value : "")}
                                options={[
                                    { value: "Rotwein", label: "Rotwein" },
                                    { value: "Weißwein", label: "Weißwein" },
                                    { value: "Dessertwein", label: "Dessertwein" },
                                    { value: "Schaumwein", label: "Schaumwein" },
                                    { value: "Portwein", label: "Portwein" },
                                    { value: "Roséwein", label: "Roséwein" }
                                ]}
                            />
                            <div className="location">
                                <Creatable
                                    className="select"
                                    classNamePrefix="select"
                                    isClearable
                                    placeholder="Land"
                                    name="land"
                                    value={formData.land ? { value: formData.land, label: formData.land } : null}
                                    onChange={(selected) => handleSelectChange("land", selected ? selected.value : "")}
                                    options={countryOptions}
                                />
                                <Creatable
                                    className="select"
                                    classNamePrefix="select"
                                    isClearable
                                    placeholder="Region"
                                    name="region"
                                    value={formData.region ? { value: formData.region, label: formData.region } : null}
                                    onChange={(selected) => handleSelectChange("region", selected ? selected.value : "")}
                                    options={regionOptions}
                                />
                            </div>
                            <div className="winery">
                                <Creatable
                                    className="select"
                                    classNamePrefix="select"
                                    isClearable
                                    placeholder="Weingut"
                                    name="weingut"
                                    value={formData.weingut ? { value: formData.weingut, label: formData.weingut } : null}
                                    onChange={(selected) => handleSelectChange("weingut", selected ? selected.value : "")}
                                    options={wineryOptions}
                                />
                            </div>
                            <div className="price">
                                <input
                                    id="preis"
                                    name="preis"
                                    type="text"
                                    placeholder="0,00"
                                    value={formData.preis}
                                    onChange={handleInputChange}
                                />
                                <label htmlFor="preis">€</label>
                            </div>
                            <div className="info">
                                <textarea
                                    name="info"
                                    placeholder="Weininfo"
                                    value={formData.info}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default Edit;